import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { CheckCircle2, Clock } from "lucide-react";

export default function OrderConfirmation() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [payStatus, setPayStatus] = useState(null); // pending | paid | expired | error
  const polled = useRef(0);

  const { data: order, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: async () => (await api.get(`/orders/${id}`)).data,
  });

  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;
    const poll = async () => {
      if (stopped || polled.current >= 8) {
        if (polled.current >= 8) setPayStatus("pending");
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayStatus("paid");
          refetch();
          return;
        }
        if (data.status === "expired") { setPayStatus("expired"); return; }
        setPayStatus("pending");
      } catch {
        setPayStatus("error");
      }
      polled.current += 1;
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, refetch]);

  if (!order) return <div className="max-w-3xl mx-auto px-6 py-20 text-center">Loading order…</div>;

  return (
    <div className="bg-white" data-testid="order-confirmation">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rh-overline mb-3">Step 03 / 03 — {payStatus === "paid" || order.payment_status === "paid" ? "Confirmed & Paid" : "Confirmed"}</div>
        <div className="flex items-center gap-3 mb-2">
          {(payStatus === "paid" || order.payment_status === "paid")
            ? <CheckCircle2 size={36} className="text-[#008a00]" />
            : <Clock size={36} className="text-[#002fa7]" />}
          <h1 className="font-display font-black text-4xl tracking-tighter">
            {(payStatus === "paid" || order.payment_status === "paid") ? "Payment received" : "Order placed"}
          </h1>
        </div>
        <p className="text-[#4a4a4a] mb-8">
          Confirmation sent to <strong>{order.shipping.email}</strong>.
          {sessionId && payStatus === "pending" && " Verifying payment with Stripe…"}
        </p>

        <div className="border border-[#0a0a0a]">
          <div className="px-6 py-4 border-b border-[#0a0a0a] flex justify-between items-center bg-[#f5f5f5]">
            <div>
              <div className="rh-overline">Order number</div>
              <div className="font-mono font-bold text-lg mt-1" data-testid="order-id">{order.order_id}</div>
            </div>
            <div className="text-right">
              <div className="rh-overline">Total</div>
              <div className="font-display font-black text-2xl rh-num mt-1">${order.total.toFixed(2)}</div>
            </div>
          </div>

          <div className="p-6 space-y-3 border-b border-[#0a0a0a]">
            {order.items.map((i) => (
              <div key={i.product_id} className="flex gap-3">
                <div className="w-14 h-14 bg-[#f5f5f5] flex-shrink-0"><img src={i.image} alt="" className="w-full h-full object-cover" /></div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{i.name}</div>
                  <div className="text-xs text-[#4a4a4a] font-mono">{i.quantity} × ${i.price.toFixed(2)}</div>
                </div>
                <div className="rh-num">${i.line_total.toFixed(2)}</div>
              </div>
            ))}
          </div>

          <div className="p-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="rh-overline mb-2">Shipping to</div>
              <div>{order.shipping.full_name}</div>
              <div>{order.shipping.address_line1}</div>
              {order.shipping.address_line2 && <div>{order.shipping.address_line2}</div>}
              <div>{order.shipping.city}, {order.shipping.state} {order.shipping.postal_code}</div>
              <div>{order.shipping.country}</div>
            </div>
            <div>
              <div className="rh-overline mb-2">Status</div>
              <div className="inline-block bg-[#0a0a0a] text-white px-3 py-1 text-xs font-semibold uppercase tracking-widest">{order.status}</div>
              <div className="rh-overline mt-4 mb-2">Payment</div>
              <div className="text-[#4a4a4a]" data-testid="payment-status">{order.payment_status}</div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex gap-4">
          <Link to="/shop" className="rh-btn-primary">Continue shopping</Link>
          <Link to="/account" className="rh-btn-secondary">View my orders</Link>
        </div>
      </div>
    </div>
  );
}
